import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { PROJECT_TYPES } from "@/data/site";

const WEB3FORMS_KEY = "efdf27bc-f47e-48e4-8eba-e33ed8fcf030";
const EMPTY = { name: "", company: "", email: "", phone: "", project_type: "", budget: "", description: "", timeframe: "", botcheck: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-display uppercase tracking-[0.2em] text-[10px] text-[#71717a]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls = "w-full bg-transparent border-b border-white/15 py-3 text-[#fafafa] placeholder-[#3f3f46] focus:border-[#d4c4a8] outline-none transition-colors duration-300";

const ThankYou = ({ open, onClose, name }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="thankyou-modal"
        className="fixed inset-0 z-[80] flex items-center justify-center p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="absolute inset-0 bg-black/85 backdrop-blur-md"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        <motion.div
          className="relative w-full max-w-lg bg-[#0c0c0c] border border-white/10 overflow-hidden"
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-[#d4c4a8]/10 blur-[90px] pointer-events-none" />
          <button data-testid="thankyou-close" onClick={onClose} className="absolute top-5 right-5 text-[#71717a] hover:text-[#fafafa] transition-colors z-10" aria-label="Schließen">
            <X size={22} strokeWidth={1.5} />
          </button>

          <div className="relative px-8 sm:px-12 py-14 text-center">
            <motion.span
              className="mx-auto mb-8 flex items-center justify-center w-20 h-20 rounded-full border border-[#d4c4a8]/50"
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
            >
              <Check size={34} className="text-[#d4c4a8]" strokeWidth={1.5} />
            </motion.span>

            <p className="label mb-5">Anfrage erhalten</p>
            <h3 className="font-serif font-light text-4xl sm:text-5xl uppercase tracking-tight leading-[0.98]">
              Vielen <span className="text-[#d4c4a8] italic">Dank.</span>
            </h3>
            <p className="mt-6 font-body text-[#a1a1aa] leading-relaxed">
              {name ? `${name}, wir` : "Wir"} haben Ihre Anfrage erhalten und melden uns
              persönlich in Kürze. Wir freuen uns auf Ihr Projekt.
            </p>
            <button data-testid="thankyou-cta" onClick={onClose} className="btn-gold mt-10 justify-center">
              Zurück zur Seite
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [sentName, setSentName] = useState("");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.botcheck) return; // honeypot
    if (!form.name || !form.email || !form.project_type || !form.description) {
      setError("Bitte Name, E-Mail, Projektart und Beschreibung ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `Neue Projektanfrage — ${form.name}`,
        from_name: "Gold Media Studio Website",
        name: form.name,
        email: form.email,
        Unternehmen: form.company || "-",
        Telefon: form.phone || "-",
        Projektart: form.project_type,
        Budget: form.budget || "-",
        Zeitraum: form.timeframe || "-",
        message: form.description,
      };
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSentName(form.name.split(" ")[0]);
        setForm(EMPTY);
        setDone(true);
      } else {
        setError("Etwas ist schiefgelaufen. Bitte erneut versuchen.");
      }
    } catch {
      setError("Etwas ist schiefgelaufen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact" className="relative py-32 sm:py-44 border-t border-white/5">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal><p className="label mb-6">Kontakt</p></Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-[0.98]">
            <MaskLine>Projekt</MaskLine>
            <MaskLine delay={0.08} className="text-[#d4c4a8] italic">starten.</MaskLine>
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 font-body text-[#a1a1aa] leading-relaxed">
              Erzählen Sie uns von Ihrer Idee. Wir melden uns persönlich und besprechen die nächsten Schritte.
            </p>
            <a href="mailto:kontakt@goldmediastudio.de" data-testid="contact-email" className="mt-6 inline-block font-display uppercase tracking-[0.14em] text-sm text-[#d4c4a8] link-underline">
              kontakt@goldmediastudio.de
            </a>
            <p className="mt-3 font-display uppercase tracking-[0.2em] text-xs text-[#71717a]">Hamburg, Deutschland</p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-8">
          <form data-testid="contact-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
            <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" className="hidden" value={form.botcheck} onChange={(e) => setForm((f) => ({ ...f, botcheck: e.target.checked ? "1" : "" }))} />
            <Field label="Name"><input data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Ihr Name" /></Field>
            <Field label="Unternehmen"><input data-testid="input-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Optional" /></Field>
            <Field label="E-Mail"><input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@firma.de" /></Field>
            <Field label="Telefon"><input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Optional" /></Field>
            <Field label="Projektart">
              <select data-testid="input-projecttype" className={`${inputCls} appearance-none`} value={form.project_type} onChange={set("project_type")}>
                <option value="" className="bg-[#111]">Bitte wählen</option>
                {PROJECT_TYPES.map((t) => <option key={t} value={t} className="bg-[#111]">{t}</option>)}
              </select>
            </Field>
            <Field label="Budget"><input data-testid="input-budget" className={inputCls} value={form.budget} onChange={set("budget")} placeholder="z. B. 5.000 – 15.000 €" /></Field>
            <div className="sm:col-span-2">
              <Field label="Projektbeschreibung">
                <textarea data-testid="input-description" rows={4} className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} placeholder="Worum geht es?" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Gewünschter Zeitraum"><input data-testid="input-timeframe" className={inputCls} value={form.timeframe} onChange={set("timeframe")} placeholder="z. B. Q3 2026" /></Field>
            </div>
            {error && <p data-testid="contact-error" className="sm:col-span-2 text-sm text-[#e26d5c]">{error}</p>}
            <div className="sm:col-span-2 mt-2">
              <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-gold disabled:opacity-50">
                {loading ? "Wird gesendet…" : "Anfrage senden"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>

      <ThankYou open={done} onClose={() => setDone(false)} name={sentName} />
    </section>
  );
};
